import { FC, Fragment } from "react";
import { IconButton, Stack } from "@mui/material";
import { WorkflowLastExecutionStatusChip } from "@/components/pages/automations/workflows/data-grid/components/columns/last-execution-cell/WorkflowLastExecutionStatusChip";
import { formatDate } from "@/utils/helpers/date-time/datetime-format";
import { Schedule } from "@/services/cloudchipr.api";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import {
  executionLogDateFilterKey,
  executionLogSourceAndExecutedByFilterKey,
} from "@/components/pages/execution-log/utils/constants/common";
import { connectSourceAndExecutedByaFilterValue } from "@/components/pages/execution-log/utils/helpers/getSourceAndExecutedByFromSelectValues";
import { getLastExecutionStatuses } from "@/components/pages/automations/workflows/data-grid/components/columns/last-execution-cell/utils/helpers/getLastExecutionStatuses";
import { useMenuHook } from "@/utils/hooks/useMenu.hook";
import { WorkflowLastExecutionPopover } from "@/components/pages/automations/workflows/data-grid/components/columns/last-execution-cell/WorkflowLastExecutionPopover";

interface WorkflowLastExecutionCellProps {
  schedule: Schedule;
}

export const WorkflowLastExecutionCell: FC<WorkflowLastExecutionCellProps> = ({
  schedule,
}) => {
  const { anchor, openMenu, closeMenu, open } = useMenuHook();

  const lastExecution = schedule.last_execution;
  const lastExecutionResults = lastExecution?.results ?? [];

  if (!lastExecution) {
    return "-";
  }

  const { succeeded, failed, skipped } =
    getLastExecutionStatuses(lastExecutionResults);
  const date = schedule.start_date_time;

  const executionLogSourceAndExecutedByaFilterValue =
    connectSourceAndExecutedByaFilterValue("workflow", schedule.name);
  const executionLogDateFilterValue = formatDate(date, {
    type: "yearMonthDay",
  });
  const executionLogsUrl = `/execution-log?${executionLogSourceAndExecutedByFilterKey}=${executionLogSourceAndExecutedByaFilterValue}&${executionLogDateFilterKey}=${executionLogDateFilterValue}`;
  const statusesCount = succeeded + failed + skipped;

  return (
    <Stack alignItems="start" spacing={0.5}>
      <TypographyWithTooltip
        variant="body2"
        title={`${formatDate(date, { type: "dateTime" })} (UTC)`}
      />

      {statusesCount > 0 && (
        <Fragment>
          <IconButton
            onClick={openMenu}
            sx={{
              p: 0.5,
              bgcolor: "grey.100",
              borderRadius: 6,
            }}
          >
            <Stack spacing={0.5} direction="row">
              <WorkflowLastExecutionStatusChip
                status="succeeded"
                count={succeeded}
              />
              <WorkflowLastExecutionStatusChip status="failed" count={failed} />
              <WorkflowLastExecutionStatusChip
                status="skipped"
                count={skipped}
              />
            </Stack>
          </IconButton>

          <WorkflowLastExecutionPopover
            open={open}
            onClose={closeMenu}
            anchor={anchor}
            lastExecutionResults={lastExecutionResults}
            lastExecutionDate={date}
            executionLogsUrl={executionLogsUrl}
          />
        </Fragment>
      )}
    </Stack>
  );
};
